import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from "../../../components/RequestDefinition";
import {Attribute} from "../../../components/ObjectAttribute";

const updateShipmentRequest: RequestExample = {
    title: {
        verb: 'POST',
        endpointUrl: '/api/shipments/:id',
    },
    curl:
        `curl --location \\
--request POST '<YOUR SERVER>/api/shipments/:id' \\
--header 'Authorization: Bearer <TOKEN>' \\
--header 'Content-Type: application/json' \\
--data-raw '{
    "id": 9,
    "note": "Updated shipment note",
    "contact": "Jane Doe",
    "carrier": {
        "id": 4,
        "service": {
            "id": 10
        }
    },
    "shipToAddress": {
        "name": "Jane Doe",
        "street": "123 Main St",
        "city": "Salt Lake City",
        "state": "UT",
        "postalCode": "84101",
        "country": "UNITED STATES"
    },
    "lastModified": {
        "dateLastModified": "2026-03-02T14:59:32.537+00:00"
    }
}'
`,
};

const updateShipmentResponse: Json = {
    title: 'RESPONSE',
    json: {
        'id': 9,
        'number': 'S10014',
        'order': {
            'id': 11,
            'number': '10014',
            'type': 'SO'
        },
        'carrier': {
            'id': 4,
            'name': 'Fedex',
            'service': {
                'id': 10,
                'name': 'Standard Overnight'
            }
        },
        'fobPointName': 'Origin',
        'locationGroup': {
            'id': 5,
            'name': 'SLC'
        },
        'lastModified': {
            'dateLastModified': '2026-03-02T15:10:45.123+00:00'
        },
        'dateCreated': '2026-01-09T23:31:28.742+00:00',
        'note': 'Updated shipment note',
        'contact': 'Jane Doe',
        'email': 'elcapitan@example.com',
        'phone': '801-932-1101',
        'billOfLading': '00000000000000093',
        'shipToAddress': {
            'name': 'Jane Doe',
            'street': '123 Main St',
            'city': 'Salt Lake City',
            'state': 'UT',
            'postalCode': '84101',
            'country': 'UNITED STATES'
        },
        'status': 'Entered',
        'cartons': [
            {
                'id': 9,
                'cartonNumber': 1,
                'trackingNumber': '1234567890',
                'freightWeight': '25.5',
                'freightAmount': '15.50',
                'sscc': '100000000000000090',
                'dateCreated': '2026-01-09T23:31:28.742+00:00',
                'width': '12',
                'height': '10',
                'length': '18',
                'sizeUom': {
                    'id': 7,
                    'name': 'Inch',
                    'abbreviation': 'IN'
                },
                'insuredValue': '500.00',
                'shipperRelease': false,
                'additionalHandling': false,
                'shipItems': [
                    {
                        'id': 10,
                        'orderItemId': 17,
                        'product': {
                            'id': 27,
                            'name': 'FG2100',
                            'description': 'Extreme Mountain Bike'
                        },
                        'tagNumber': '369',
                        'quantity': '1',
                        'uom': {
                            'id': 1,
                            'name': 'Each',
                            'abbreviation': 'ea'
                        },
                        'totalCost': '0'
                    }
                ]
            }
        ],
        'customFields': [
            {
                'id': 21,
                'name': 'Custom',
                'type': 'Text',
                'value': 'Updated value'
            }
        ]
    },
};

const updateShipmentAttributes: Attribute[] = [
    {
        name: 'id',
        type: 'integer',
        optional: true,
        description: 'The shipment\'s unique identification number. If provided, must match the ID in the URL path.'
    },
    {
        name: 'note',
        type: 'string',
        optional: true,
        description: 'The shipment\'s note field.'
    },
    {
        name: 'contact',
        type: 'string',
        optional: true,
        description: 'The contact name for the shipment.'
    },
    {
        name: 'carrier',
        type: 'object',
        optional: true,
        description: 'The carrier information for the shipment.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The carrier\'s unique ID.'
            },
            {
                name: 'service',
                type: 'object',
                optional: true,
                description: 'The carrier service.',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The carrier service ID.'
                    }
                ]
            }
        ]
    },
    {
        name: 'shipToAddress',
        type: 'object',
        optional: true,
        description: 'The shipping address for the shipment. On a shipped shipment this is the only shipment-level field that can be changed, and only by a user with the "Always Edit Address" access right; without that right it is ignored.',
        children: [
            {
                name: 'name',
                type: 'string',
                optional: true,
                description: 'The recipient name.'
            },
            {
                name: 'street',
                type: 'string',
                optional: true,
                description: 'The street address.'
            },
            {
                name: 'city',
                type: 'string',
                optional: true,
                description: 'The city.'
            },
            {
                name: 'state',
                type: 'string',
                optional: true,
                description: 'The state or province code.'
            },
            {
                name: 'postalCode',
                type: 'string',
                optional: true,
                description: 'The postal/ZIP code.'
            },
            {
                name: 'country',
                type: 'string',
                optional: true,
                description: 'The country name.'
            }
        ]
    },
    {
        name: 'locationGroup',
        type: 'reference object',
        optional: true,
        description: 'The location group for the shipment.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The location group ID.'
            }
        ]
    },
    {
        name: 'lastModified',
        type: 'object',
        description: 'The last modified timestamp. Used for concurrency control to prevent conflicting updates.',
        children: [
            {
                name: 'dateLastModified',
                type: 'date',
                description: 'The timestamp when the shipment was last modified. Must match the current value to proceed with the update.'
            }
        ]
    },
    {
        name: 'cartons',
        type: 'list',
        optional: true,
        description: 'A list of cartons/packages in the shipment. When updating cartons, total quantities per item must remain the same (items may be redistributed among cartons but not added or removed).',
        children: [
            {
                name: 'id',
                type: 'integer',
                optional: true,
                description: 'The carton ID (required for updating existing cartons, omit for new cartons).'
            },
            {
                name: 'trackingNumber',
                type: 'string',
                optional: true,
                description: 'The tracking number for this carton. On a shipped shipment this is the only carton field that can be changed, and only by a user with the "Always Edit Carton Tracking Number" access right; without that right it is ignored.'
            },
            {
                name: 'freightWeight',
                type: 'decimal',
                optional: true,
                description: 'The weight of the carton.'
            },
            {
                name: 'freightAmount',
                type: 'decimal',
                optional: true,
                description: 'The freight cost for this carton.'
            },
            {
                name: 'width',
                type: 'decimal',
                optional: true,
                description: 'The width of the carton.'
            },
            {
                name: 'height',
                type: 'decimal',
                optional: true,
                description: 'The height of the carton.'
            },
            {
                name: 'length',
                type: 'decimal',
                optional: true,
                description: 'The length of the carton.'
            },
            {
                name: 'sizeUom',
                type: 'reference object with abbreviation',
                optional: true,
                description: 'The unit of measure for the carton dimensions. The UOM is resolved by id, and its code must be "IN" or "CM" (case-insensitive).',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        description: 'The unique identification number. Used to resolve and validate the UOM.'
                    },
                    {
                        name: 'name',
                        type: 'string',
                        optional: true,
                        description: 'The name of the unit of measure.'
                    },
                    {
                        name: 'abbreviation',
                        type: 'string',
                        optional: true,
                        description: 'The abbreviated name. Not read by this endpoint — only the sizeUom id is used to resolve and validate the UOM.'
                    }
                ]
            },
            {
                name: 'shipItems',
                type: 'list',
                optional: true,
                description: 'The items shipped in this carton. Total quantity per order item across all cartons must remain the same; items may be moved between cartons but not added or removed.',
                children: [
                    {
                        name: 'id',
                        type: 'integer',
                        optional: true,
                        description: 'The ship item ID. Required to reposition an existing item; omit when splitting an item into a new ship item.'
                    },
                    {
                        name: 'orderItemId',
                        type: 'integer',
                        optional: true,
                        description: 'The order item ID (sales order, purchase order, or transfer order item) this ship item fulfills. Required when creating a new ship item (id omitted); ignored when repositioning an existing item by id.'
                    },
                    {
                        name: 'product',
                        type: 'reference object',
                        optional: true,
                        description: 'The product being shipped (only for sales order shipments). Required when creating a new ship item (id omitted) on a sales order shipment; ignored when repositioning an existing item by id.'
                    },
                    {
                        name: 'part',
                        type: 'reference object',
                        optional: true,
                        description: 'The part being shipped (only for purchase and transfer order shipments). Required when creating a new ship item (id omitted) on a purchase or transfer order shipment; ignored when repositioning an existing item by id.'
                    },
                    {
                        name: 'quantity',
                        type: 'decimal',
                        optional: true,
                        description: 'The quantity of this item shipped in this carton. Required when creating a new ship item (id omitted); when repositioning an existing item by id it defaults to the item\'s current quantity if omitted.'
                    },
                    {
                        name: 'tagNumber',
                        type: 'string',
                        optional: true,
                        description: 'Read-only. Ignored on input: a new ship item automatically inherits the inventory tag of the existing ship item(s) for its order item. Any value sent here is disregarded. If that order item was shipped from more than one tag, the request is rejected with a 400, because there is no way to indicate which tag the new item should inherit — adjust the quantity of the existing ship items instead.'
                    },
                    {
                        name: 'uom',
                        type: 'reference object',
                        optional: true,
                        description: 'The unit of measure for the shipped quantity. Required when creating a new ship item (e.g. splitting a tag-tracked item into a new carton); when repositioning an existing item by id it defaults to the item\'s current UOM.'
                    }
                ]
            }
        ]
    },
    {
        name: 'customFields',
        type: 'list',
        optional: true,
        description: 'A list of custom fields associated with the shipment.',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The custom field ID.'
            },
            {
                name: 'value',
                type: 'string',
                optional: true,
                description: 'The custom field value.'
            }
        ]
    }
];

const data: RequestDefinitionData = {
    id: 'update-shipment',
    title: 'Update a shipment',
    description: 'Updates an existing shipment. You can update shipment details such as notes, contact information, carrier, shipping address, and carton information. The shipment must be in "Entered" or "Packed" status for a full update. A "Shipped" shipment is closed to edits except for two fields: a carton tracking number, which requires the "Always Edit Carton Tracking Number" access right, and the ship-to address, which requires the "Always Edit Address" access right. A user holding neither right cannot update a shipped shipment at all (403). On a shipped shipment every other field the request carries is ignored - including a new carton, a removed carton and any ship item change - so a response from GET /shipments/:id can be posted back safely and only those two values will be applied. When updating cartons, total quantities per item must remain the same - items may be moved between cartons but not added or removed. The lastModified timestamp must be provided to prevent concurrent modification conflicts. Requires SHIP_PACK access right.',
    endpoint: {
        verb: 'POST',
        path: '/shipments/:id',
        tooltip: 'Update a shipment',
    },
    attributes: updateShipmentAttributes,
    requestObject: updateShipmentRequest,
    responseObject: updateShipmentResponse,
};

export default data;
